import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@kmgestao/ui';

import { useSoftDeleteLead } from '../../../features/leads/index.js';

export interface DeleteLeadDialogProps {
  leadId: string;
  leadName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/**
 * Dialog de confirmação de exclusão. Usa `useSoftDeleteLead` (set
 * `deleted_at`), que mantém o registro para auditoria LGPD + timeline
 * de lead_activities permanece consultável. Lead some da UI via
 * filtro `deleted_at IS NULL` nas queries.
 *
 * Diferença de `MarkAsLostDialog`: "perdido" é estado de pipeline
 * (Karla não fechou negócio), "excluir" é remover do CRM (entrada
 * duplicada, criada por engano, sem contato). Cada um tem semântica
 * diferente — mantemos separados.
 */
export function DeleteLeadDialog({
  leadId,
  leadName,
  open,
  onOpenChange,
  onDeleted,
}: DeleteLeadDialogProps) {
  const softDelete = useSoftDeleteLead();

  const onConfirm = async () => {
    await softDelete.mutateAsync(leadId, {
      onSuccess: () => {
        toast.success(`Lead "${leadName}" excluído.`);
        onOpenChange(false);
        onDeleted?.();
      },
      onError: (err) =>
        toast.error('Falha ao excluir lead', { description: (err as Error).message }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir lead</DialogTitle>
          <DialogDescription>
            Excluir <strong className="text-foreground">{leadName}</strong>? O registro é mantido no
            banco para auditoria (LGPD), mas some da listagem. Para arquivar um lead que não fechou
            negócio, prefira <em>Marcar como perdido</em>.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg" disabled={softDelete.isPending}>
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            size="lg"
            onClick={onConfirm}
            disabled={softDelete.isPending}
          >
            {softDelete.isPending ? 'Excluindo…' : 'Excluir lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
